import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { ListChecks, Loader2, Inbox } from 'lucide-react'
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuTrigger,
  DropdownMenuLabel, DropdownMenuSeparator,
} from '@meno/ui/components/ui/dropdown-menu'
import { useTaskCenter, isUploadTask } from '@/contexts/TaskCenterContext'
import { RunningRow, TaskRow } from '@/components/TaskCenter/taskRows'

// TopBar Task Center — the task feed in an icon-anchored popover, opened from
// the ListChecks button just like the NotificationBell dropdown. Self-contained
// (owns its trigger + badge + open state) so the popover anchors to the icon.
// Shares its row rendering with the full /tasks page via
// components/TaskCenter/taskRows.
//
// It stays passive like the NotificationBell: a live count badge sits on the
// button while work is in flight and clears itself once every job settles
// (activeCount → 0, incl. the server sweep aging a stuck publish to failed).
// The panel only opens when the operator clicks the button — it never pops
// itself open "on top" of the page. `modal={false}` keeps the rest of the page
// interactive while the feed is open.
export default function TaskCenter() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { tasks, runningTasks, activeCount } = useTaskCenter()
  const [open, setOpen] = useState(false)

  // The Task Center is a live work queue, not a history log: show only what's
  // still in flight. Client uploads render as progress bars (RunningRow); active
  // server tasks (pending / running) render as cards (TaskRow). Completed and
  // failed tasks live on the full /tasks page.
  const uploadTasks = runningTasks.filter(isUploadTask)
  const activeTasks = tasks.filter(t => t.status === 'pending' || t.status === 'running')

  const openFullPage = () => {
    setOpen(false)
    navigate('/activity')
  }

  return (
    <DropdownMenu open={open} onOpenChange={setOpen} modal={false}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={t('activity.title', 'Activity Center')}
          className="relative flex items-center justify-center w-8 h-8 rounded-md text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent transition-colors"
        >
          <ListChecks className="w-[14px] h-[14px]" />
          {activeCount > 0 && (
            <span className="absolute -top-1 -end-1 min-w-[15px] h-[15px] px-[3px] flex items-center justify-center rounded-full bg-red-600 text-white text-[9px] font-bold leading-none ring-2 ring-sidebar">
              {activeCount > 99 ? '99+' : activeCount}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-[22rem] p-0">
        {/* Header — title + active count, matching the notifications popover. */}
        <div className="flex items-center justify-between px-3 py-2.5">
          <DropdownMenuLabel className="p-0 text-sm font-semibold">
            {t('activity.title', 'Activity Center')}
          </DropdownMenuLabel>
          {activeCount > 0 && (
            <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-semibold tabular-nums text-muted-foreground">
              {activeCount > 99 ? '99+' : activeCount} {t('tasks.active', 'active')}
            </span>
          )}
        </div>
        <DropdownMenuSeparator className="my-0" />

        {/* Body — active work only (uploads + pending/running tasks); scrolls
            within the popover. Completed history lives on the /tasks page. */}
        <div className="max-h-[70vh] overflow-y-auto p-3 space-y-4">
          {uploadTasks.length > 0 && (
            <div className="rounded-xl border border-primary/30 bg-muted p-3">
              <div className="flex items-center gap-2 mb-3">
                <Loader2 className="w-4 h-4 text-primary animate-spin" />
                <h2 className="text-sm font-semibold">{t('tasks.uploading', 'Uploading')}</h2>
                <span className="text-xs text-muted-foreground">({uploadTasks.length})</span>
              </div>
              <div className="space-y-3">
                {uploadTasks.map(rt => (
                  <RunningRow key={rt.id} task={rt} />
                ))}
              </div>
            </div>
          )}

          {activeTasks.length > 0 && (
            <div>
              <h2 className="text-sm font-semibold mb-2 px-1">{t('tasks.activeTitle', 'Active tasks')}</h2>
              <div className="space-y-2">
                {activeTasks.map(task => <TaskRow key={task.id} task={task} />)}
              </div>
            </div>
          )}

          {uploadTasks.length === 0 && activeTasks.length === 0 && (
            <div className="rounded-xl border border-dashed border-border p-8 text-center">
              <Inbox className="w-7 h-7 text-muted-foreground mx-auto mb-2" />
              <p className="text-xs text-muted-foreground">
                {t('tasks.emptyActive', 'Nothing is publishing or uploading right now.')}
              </p>
            </div>
          )}
        </div>

        {/* Footer — jump to the full-page feed. */}
        <DropdownMenuSeparator className="my-0" />
        <div className="flex items-center justify-end px-3 py-2">
          <button
            type="button"
            onClick={openFullPage}
            className="rounded-md px-2.5 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          >
            {t('tasks.viewAll', 'View all')}
          </button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
